import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, StickyNote, Pencil } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

const STAGE_COLOR = {
  Contacté: "#94A3B8",
  Intéressé: "#0EA5E9",
  "NDA envoyé": "#8B5CF6",
  "NDA signé": "#C6A87C",
  "Dossier transmis": "#0F172A",
  Négociation: "#F59E0B",
  Accord: "#10B981",
  Refus: "#EF4444",
};

export default function Pipeline() {
  const { t } = useTranslation();
  const [stages, setStages] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [notes, setNotes] = useState("");
  const [reminder, setReminder] = useState("");

  const load = () =>
    api.get("/pipeline").then((r) => {
      setStages(r.data.stages);
      setItems(r.data.items);
    });

  useEffect(() => {
    load();
  }, []);

  const changeStage = async (item, stage) => {
    setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, stage } : p)));
    try {
      await api.patch(`/pipeline/${item.id}`, { stage });
      toast.success(t("pipeline.movedTo", { stage }));
    } catch {
      toast.error(t("pipeline.updateError"));
      load();
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setNotes(item.notes || "");
    setReminder(item.reminder || "");
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/pipeline/${editing.id}`, { notes, reminder });
      setItems((prev) => prev.map((p) => (p.id === editing.id ? { ...p, notes, reminder } : p)));
      toast.success(t("pipeline.cardUpdated"));
      setEditing(null);
    } catch {
      toast.error(t("pipeline.saveError"));
    }
  };

  return (
    <div className="px-6 py-10 lg:px-12" data-testid="pipeline-page">
      <header className="mb-8">
        <p className="overline text-[#C6A87C]">{t("pipeline.overline")}</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">{t("pipeline.title")}</h1>
        <p className="mt-2 text-slate-500">{t("pipeline.subtitle")}</p>
      </header>

      <div className="flex gap-5 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const cards = items.filter((i) => i.stage === stage);
          return (
            <div key={stage} className="min-w-[300px] flex-1" data-testid={`stage-${stage}`}>
              <div className="mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: STAGE_COLOR[stage] }} />
                  {stage}
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{cards.length}</span>
              </div>
              <div className="space-y-3">
                {cards.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`pipeline-card-${item.id}`}
                    className="rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md"
                    style={{ borderLeft: `3px solid ${STAGE_COLOR[stage]}` }}
                  >
                    <div className="flex items-start gap-3">
                      <img src={item.investor_avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#0F172A]">{item.investor_name}</p>
                        <p className="truncate text-xs text-slate-400">{item.opportunity_title}</p>
                      </div>
                      <button onClick={() => openEdit(item)} data-testid={`edit-card-${item.id}`} className="text-slate-300 hover:text-[#C6A87C]">
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>

                    {item.notes && (
                      <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
                        <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {item.notes}
                      </p>
                    )}
                    {item.reminder && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-600">
                        <Bell className="h-3.5 w-3.5" /> {t("pipeline.reminderOn", { date: item.reminder })}
                      </p>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`move-card-${item.id}`}
                          className="mt-3 w-full rounded-lg border border-slate-200 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-[#C6A87C] hover:text-[#0F172A]"
                        >
                          {t("pipeline.changeStage")}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {stages.map((s) => (
                          <DropdownMenuItem key={s} onClick={() => changeStage(item, s)} data-testid={`move-to-${item.id}-${s}`}>
                            {s}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
                {cards.length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-xs text-slate-300">
                    {t("pipeline.noDeal")}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0F172A]">{t("pipeline.notesTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("pipeline.note")}</Label>
              <Textarea data-testid="edit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" rows={3} />
            </div>
            <div>
              <Label>{t("pipeline.reminderDate")}</Label>
              <Input type="date" data-testid="edit-reminder" value={reminder} onChange={(e) => setReminder(e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="edit-save" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
